/**
 * Strawa brand theme -- single source of truth is brand/tokens.json at the
 * repository root. The JSON is imported at build time so console styling can
 * never drift from the canonical brand tokens.
 */
import tokens from "../../../../brand/tokens.json";

export type ThemeMode = "light" | "dark";

export type RiskLevel = "green" | "yellow" | "red";

export const brandTokens = tokens;

const THEME_STORAGE_KEY = "strawatar-console-theme";

/** Map a risk tier to the CSS custom property carrying its color. */
export function riskCssVar(risk: RiskLevel): string {
  switch (risk) {
    case "red":
      return "--emergency";
    case "yellow":
      return "--warning";
    case "green":
      return "--success";
  }
}

/** German label for a risk tier. */
export function riskLabel(risk: RiskLevel): string {
  switch (risk) {
    case "red":
      return "Rot";
    case "yellow":
      return "Gelb";
    case "green":
      return "Grün";
  }
}

/**
 * Apply the brand tokens for the given mode as CSS custom properties on
 * the document root. All values come verbatim from brand/tokens.json.
 */
export function applyTheme(mode: ThemeMode): void {
  const vars = tokens["css-custom-properties"][mode];
  const root = document.documentElement;
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  // Derived properties used by the console layout.
  root.style.setProperty("--font-family", tokens.typography["font-family"]);
  root.style.setProperty("--sidebar-bg", tokens.colors.dark.hsl);
  root.style.setProperty("--brand-primary-hex", tokens.colors.primary.hex);
  root.style.setProperty("--brand-accent-hex", tokens.colors.accent.hex);
  root.dataset.theme = mode;
}

/** Gespeicherte Wahl, sonst die helle Darstellung. */
export function storedThemeMode(): ThemeMode {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    // localStorage unavailable: fall through to the system preference.
  }
  if (stored === "dark" || stored === "light") return stored;
  return "light";
}

export function persistThemeMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // localStorage unavailable (private mode) -- theme stays session-only.
  }
}
